import React, { useEffect, useRef, useState } from "react";

import ChevronDownIcon from "@uui/ChevronDown";

import {
  Accordion,
  AccordionProps,
  AccordionSummary,
  SxProps,
  Typography,
} from "@mui/material";
import { AccordionDetails } from "@mui/material";

import Stack, { Row } from "@rui/Stack";

interface SettingAccordionProps extends AccordionProps {
  title: string;
  description?: React.ReactNode;
  detailsSx?: SxProps;
  expandDisabled?: boolean;
  Toggle?: React.ReactNode;
}

const SettingAccordion = ({
  sx,
  title,
  description,
  detailsSx,
  Toggle,
  expandDisabled = false,
  ...props
}: SettingAccordionProps) => {
  const accordionSummary = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState<boolean>(
    expandDisabled ? false : props.defaultExpanded ?? false
  );

  const handleExpandAccordion = (event: React.SyntheticEvent) => {
    // Used to stop the accordion from expanding every time you click a toggle
    // event.stopPropagation doesn't work here for some reason.
    if ((event.target as HTMLElement).tagName === "INPUT" || expandDisabled)
      return;

    if (!expandDisabled) {
      setExpanded((e) => !e);
    }
  };

  useEffect(() => {
    if (expandDisabled) {
      setExpanded(false);
    }
    setExpanded(true);
  }, [expandDisabled]);

  return (
    <Accordion
      elevation={0}
      sx={{
        borderRadius: 0,
        "&.Mui-expanded": { my: 0 },
        "&:before": { backgroundColor: "shades.neutral.50" },
      }}
      disableGutters
      expanded={!expandDisabled && expanded}
      {...props}
    >
      <AccordionSummary
        sx={{
          px: 0,
          pt: 1,
          pb: expanded ? 0 : 1,
          width: "100%",
          ...sx,
        }}
        onClick={handleExpandAccordion}
        ref={accordionSummary}
      >
        <Stack spacing={0.5} sx={{ width: "100%" }}>
          <Row justifyContent="space-between" spacing={2}>
            <Stack spacing={0.5}>
              <Row spacing={1} variant="left">
                <Typography variant="bodyPrimaryMed" sx={{ my: 0.5 }}>
                  {title}
                </Typography>
                {!expandDisabled && (
                  <ChevronDownIcon
                    sx={{
                      fontSize: "18px",
                      transform: expanded ? "rotate(-180deg)" : "none",
                      transition: "transform 0.2s ease",
                    }}
                  />
                )}
              </Row>
              {description && (
                <Typography
                  variant="bodySecondaryReg"
                  sx={{ color: "shades.neutral.500" }}
                >
                  {description}
                </Typography>
              )}
            </Stack>
            {Toggle && Toggle}
          </Row>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pb: 2, pt: 0, ...detailsSx }}>
        {props.children}
      </AccordionDetails>
    </Accordion>
  );
};

export default SettingAccordion;
